import React from "react";
import { Alert, AlertTitle, Stack, Typography } from "@mui/material";
import AgentLaunchpadDialog from "@hyro-components/Dialogs/agent-launchpad/AgentLaunchpadDialog";
import { DIALOG_VARIANTS } from "@hyro-components/Dialogs/DialogConsts";
import HyroLoader from "@hyro-components/HyroLoader";
import HyroLinearProgressBar from "@hyro-components/HyroLinearProgressBar";
import HyroButtonGroup from "@hyro-components/hyro-button-group/HyroButtonGroup";
import { HyroButton } from "@hyro-components";
import { BUTTON_SIZES, BUTTON_VARIANTS } from "@hyro-components/hyro-button/HyroButton.consts";
import ReviewFilters from "./ReviewFilters";
import ReviewGroup from "./ReviewGroup";
import useReviewChangesDialog from "./ReviewChangesDialog.hook";
import { REVIEW_COPY, REVIEW_TEST_ID } from "./reviewChanges.consts";
import { CompareTo, TopBar } from "./ReviewChangesDialog.styles";

const CenteredMessage = ({ testId, title, body, children }) => (
  <Stack data-testid={testId} alignItems="center" gap={1} sx={{ py: 6, textAlign: "center" }}>
    <Typography variant="subtitle1" color="text.800">{title}</Typography>
    <Typography variant="body2" color="text.600">{body}</Typography>
    {children}
  </Stack>
);

// Lists the draft's pending changes before Build or Publish. `onConfirm` runs the real action; the dialog
// never blocks it (empty, failed or partial reviews all keep it available — "Continue anyway" on failure),
// it only waits while a review is loading.
const ReviewChangesDialog = ({ open, gate, onConfirm, onCancel, confirmLoading = false }) => {
  const {
    base,
    groups,
    counts,
    changeFilters,
    toggleChangeFilter,
    clearChangeFilters,
    isLoading,
    isError,
    isEmpty,
    showBaseSwitch,
    baseOptions,
    selectedBaseOption,
    handleBaseChange,
    disabledBaseOptions,
    baseOptionTooltips,
    showKnowledgeBuildNote,
    showKnowledgeOmittedNote,
    showLiveBase,
    showKnowledgeError,
    retry,
    isRefreshing
  } = useReviewChangesDialog({ open, gate });
  const hasChanges = !isLoading && !isError && !isEmpty;

  return (
    <AgentLaunchpadDialog
      variant={DIALOG_VARIANTS.transactional}
      open={open}
      maxWidth="lg"
      testId={REVIEW_TEST_ID}
      title={REVIEW_COPY.TITLES[gate]}
      submitButtonText={isError ? REVIEW_COPY.CONTINUE_ANYWAY : REVIEW_COPY.submitLabel(gate, counts.all)}
      submitButtonLoading={confirmLoading}
      disableSubmit={isRefreshing}
      handleSubmit={onConfirm}
      handleCancel={onCancel}
      handleClose={onCancel}
      onClose={onCancel}
      isLoading={isLoading}
    >
      <Stack gap={2} sx={{ pt: 1 }}>
        {/* While loading only the loader shows: no toolbar, and the dialog hides its footer. */}
        {!isLoading && (
          <TopBar>
            {hasChanges
              ? <ReviewFilters counts={counts} selected={changeFilters} onToggle={toggleChangeFilter} onClear={clearChangeFilters} />
              : <span />}
            {showBaseSwitch && (
              <CompareTo>
                <Typography variant="body2" color="text.700">{REVIEW_COPY.COMPARE_TO}</Typography>
                <HyroButtonGroup
                  options={baseOptions}
                  selectedOption={selectedBaseOption}
                  onChange={handleBaseChange}
                  disabledOptions={disabledBaseOptions}
                  optionTooltips={baseOptionTooltips}
                  disableEmpty
                />
              </CompareTo>
            )}
            {isRefreshing && <HyroLinearProgressBar variant="indeterminate" hideLabel barHeight={2} data-testid={`${REVIEW_TEST_ID}-refreshing`} />}
          </TopBar>
        )}
        {showKnowledgeBuildNote && (
          <Alert
            severity="info"
            data-testid={`${REVIEW_TEST_ID}-knowledge-note`}
            action={<HyroButton variant={BUTTON_VARIANTS.TEXT} size={BUTTON_SIZES.SMALL} onClick={showLiveBase}>{REVIEW_COPY.SHOW_LIVE}</HyroButton>}
          >
            {REVIEW_COPY.KNOWLEDGE_BUILD_NOTE}
          </Alert>
        )}
        {showKnowledgeOmittedNote && (
          <Alert severity="info" data-testid={`${REVIEW_TEST_ID}-knowledge-omitted`}>{REVIEW_COPY.KNOWLEDGE_OMITTED_NOTE}</Alert>
        )}
        {showKnowledgeError && (
          <Alert
            severity="warning"
            data-testid={`${REVIEW_TEST_ID}-knowledge-error`}
            action={(
              <HyroButton variant={BUTTON_VARIANTS.TEXT} size={BUTTON_SIZES.SMALL} loading={isRefreshing} onClick={retry}>
                {REVIEW_COPY.RETRY}
              </HyroButton>
            )}
          >
            <AlertTitle>{REVIEW_COPY.KNOWLEDGE_ERROR_TITLE}</AlertTitle>
            {REVIEW_COPY.KNOWLEDGE_ERROR_BODY}
          </Alert>
        )}
        {isLoading && <HyroLoader text={REVIEW_COPY.LOADING} height="240px" />}
        {isError && (
          <CenteredMessage testId={`${REVIEW_TEST_ID}-error`} title={REVIEW_COPY.ERROR_TITLE} body={REVIEW_COPY.ERROR_BODY}>
            <HyroButton variant={BUTTON_VARIANTS.TEXT} onClick={retry}>{REVIEW_COPY.RETRY}</HyroButton>
          </CenteredMessage>
        )}
        {isEmpty && (
          <CenteredMessage testId={`${REVIEW_TEST_ID}-empty`} title={REVIEW_COPY.EMPTY_TITLES[base]} body={REVIEW_COPY.EMPTY_BODIES[base]} />
        )}
        {hasChanges && (
          <Stack gap={2} data-testid={`${REVIEW_TEST_ID}-groups`}>
            {/* Everything starts collapsed; selecting a filter re-opens every group and resource it shows. */}
            {groups.map((group) => (
              <ReviewGroup
                key={`${group.key}-${changeFilters.join(",")}`}
                group={group}
                expanded={changeFilters.length > 0}
              />
            ))}
          </Stack>
        )}
      </Stack>
    </AgentLaunchpadDialog>
  );
};

export default ReviewChangesDialog;
